import { addDays, addSeconds, differenceInCalendarDays, differenceInSeconds, format, parseISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

const isoFormat = "yyyy-MM-dd'T'HH:mm:ssXXX";
const dateFormat = 'yyyy-MM-dd';
const timeFormat = 'HH:mm:ss.SSS';

function isDateAnchored(event) {
  return event.all_day || (event.event_type === 'tba_game' && event._embedded?.tba_game?.has_time === false);
}

function dateInTimeZone(date, timezone, fallback) {
  return timezone ? formatInTimeZone(date, timezone, dateFormat) : fallback;
}

function continuationInstant(start, date, timezone, dayOffset) {
  if (!timezone) {
    return addDays(start, dayOffset);
  }
  const time = formatInTimeZone(start, timezone, timeFormat);
  return zonedTimeToUtc(`${date}T${time}`, timezone);
}

function eventDurationSeconds(event) {
  const start = parseISO(event.iso8601);
  const end = parseISO(event.end_iso8601);
  if (!event.recurring) {
    return differenceInSeconds(end, start);
  }
  // For a recurring event, end_iso8601 is the end of the event on the final
  // day. The setUTC* methods are used in case there's a daylight savings time
  // shift between the start and end dates
  const firstOccurrenceEnd = new Date(start);
  firstOccurrenceEnd.setUTCHours(end.getUTCHours());
  firstOccurrenceEnd.setUTCMinutes(end.getUTCMinutes());
  firstOccurrenceEnd.setUTCSeconds(end.getUTCSeconds());
  return differenceInSeconds(firstOccurrenceEnd, start);
}

export default function expandEvents(events, occurrencesByEventId) {
  const rescheduledDatesByEventId = {};
  for (let event of events) {
    if (event.recurring || !event.icalendar_parent_id) {
      continue;
    }
    if (!rescheduledDatesByEventId[event.icalendar_parent_id]) {
      rescheduledDatesByEventId[event.icalendar_parent_id] = [event.icalendar_original_date];
    } else {
      rescheduledDatesByEventId[event.icalendar_parent_id].push(event.icalendar_original_date);
    }
  }

  return events.flatMap(event => {
    if (!event.recurring) {
      return event;
    }
    const durationSeconds = eventDurationSeconds(event);
    return occurrencesByEventId[event.id].reduce((memo, occurrenceString) => {
      const occurrence = parseISO(occurrenceString);
      const ocurrenceEnd = addSeconds(occurrence, durationSeconds);
      const e = {...event};
      e.iso8601 = occurrence.toISOString();
      e.end_iso8601 = ocurrenceEnd.toISOString();
      if (e.tzid) {
        e.local_iso8601 = formatInTimeZone(occurrence, e.tzid, isoFormat);
        e.local_end_iso8601 = formatInTimeZone(ocurrenceEnd, e.tzid, isoFormat);
      } else {
        e.local_iso8601 = e.iso8601;
        e.local_end_iso8601 = e.end_iso8601;
      }
      const localDate = e.local_iso8601.slice(0, 10);
      if (rescheduledDatesByEventId[event.id] && rescheduledDatesByEventId[event.id].indexOf(localDate) >= 0) {
        return memo;
      }
      memo.push(e);
      return memo;
    }, []);
  });
}

export const addNewEvents = function (eventList, scheduleTimezone) {
  const newEventList = [];
  for (const e of eventList) {
    newEventList.push(e);
    if(e.event_type === "game") {
      continue;
    }
    const dateAnchored = isDateAnchored(e);
    const timezone = dateAnchored ? e.tzid : scheduleTimezone || e.tzid;
    const parsedStartTime = parseISO(e.iso8601);
    const parsedEndTime = parseISO(e.end_iso8601);
    const coveredEndTime = !dateAnchored && parsedEndTime > parsedStartTime
      ? new Date(parsedEndTime.getTime() - 1)
      : parsedEndTime;
    const localStartDate = parseISO(dateAnchored
      ? e.local_iso8601.slice(0, 10)
      : dateInTimeZone(parsedStartTime, timezone, e.local_iso8601.slice(0, 10)));
    // A date-only end names the sheet's final date. Converting its stored
    // 23:59:59 instant to the schedule's zone can add an unintended day.
    const localEndDate = parseISO(dateAnchored || e.ends_date_only
      ? e.local_end_iso8601.slice(0, 10)
      : dateInTimeZone(coveredEndTime, timezone, e.local_end_iso8601.slice(0, 10)));
    const diff = differenceInCalendarDays(localEndDate, localStartDate);
    if (diff < 1) {
      continue;
    }
    for (let i = 1; i <= diff; i++) {
      const continuationDate = format(addDays(localStartDate, i), dateFormat);
      const newDay = continuationInstant(parsedStartTime, continuationDate, timezone, i);
      let newEvent = {
        ...e,
        iso8601: newDay.toISOString(),
        local_iso8601: e.tzid ? formatInTimeZone(newDay, e.tzid, isoFormat) : newDay.toISOString(),
        multi_day: true,
      };
      if (i === diff) {
        newEvent.final_day = true;
      }
      newEventList.push(newEvent);
    }
  }
  return newEventList;
}
